import React, { useEffect, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, PhoneFill, Display, ChevronDown, ClockHistory, BoxArrowUp, GripVertical, ArrowRight } from 'react-bootstrap-icons';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { SvgArtifact } from '../utils/svgDetector';
import { useUsage } from '../contexts/UsageContext';
import usageService from '../services/usageService';
import AlertBanner from './AlertBanner';
import FigmaIntegrationGuide from './FigmaIntegrationGuide';
import './SvgCanvas.css';

interface SvgCanvasProps {
  svgCode: string;
  onClose: () => void;
  width?: number;
  onWidthChange?: (width: number) => void;
  rightOffset?: number;
  title?: string;
  templateName?: string;
  artifacts?: SvgArtifact[];
  selectedArtifactIndex?: number;
  onArtifactSelect?: (index: number) => void;
  isStreaming?: boolean;
}

type ViewMode = 'mobile' | 'desktop';

const SvgCanvas: React.FC<SvgCanvasProps> = ({
  svgCode,
  onClose,
  width = 45,
  onWidthChange,
  rightOffset = 0,
  title,
  templateName,
  artifacts = [],
  selectedArtifactIndex = 0,
  onArtifactSelect,
  isStreaming = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const { canExportFigma, getFigmaExportLimitMessage, trackFigmaExport } = useUsage();
  const [error, setError] = useState<string | null>(null);
  const [figmaLimitError, setFigmaLimitError] = useState<string | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(width);
  const [zoomLevel, setZoomLevel] = useState(50);
  const [viewMode, setViewMode] = useState<ViewMode>('mobile');
  const [hasMobileSvg, setHasMobileSvg] = useState(false);
  const [hasDesktopSvg, setHasDesktopSvg] = useState(false);
  const [mobileSvg, setMobileSvg] = useState<Element | null>(null);
  const [desktopSvg, setDesktopSvg] = useState<Element | null>(null);
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showFigmaGuide, setShowFigmaGuide] = useState(false);
  const [showFigmaAlert, setShowFigmaAlert] = useState(false);

  // Parse SVGs and detect mobile/desktop versions
  useEffect(() => {
    // Don't set error if we're streaming - the skeleton loader will show instead
    if (!svgCode) {
      if (!isStreaming) {
        setError('No SVG code provided');
      }
      // Reset SVG state when svgCode is empty
      setMobileSvg(null);
      setDesktopSvg(null);
      setHasMobileSvg(false);
      setHasDesktopSvg(false);
      return;
    }

    try {
      // Create a temporary div to parse the SVG
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = svgCode.trim();

      // Look for SVGs with id="mobile" and id="desktop"
      const mobileElement = tempDiv.querySelector('svg[id="mobile"]');
      const desktopElement = tempDiv.querySelector('svg[id="desktop"]');

      // If no specific IDs, check for any SVG
      const anySvgElement = tempDiv.querySelector('svg');

      let foundAtLeastOne = false;

      if (mobileElement) {
        setMobileSvg(mobileElement.cloneNode(true) as Element);
        setHasMobileSvg(true);
        foundAtLeastOne = true;
      } else {
        setMobileSvg(null);
        setHasMobileSvg(false);
      }

      if (desktopElement) {
        setDesktopSvg(desktopElement.cloneNode(true) as Element);
        setHasDesktopSvg(true);
        foundAtLeastOne = true;
      } else {
        setDesktopSvg(null);
        setHasDesktopSvg(false);
      }

      // If no mobile/desktop specific SVGs found, use any SVG for mobile view
      if (!mobileElement && !desktopElement && anySvgElement) {
        const clonedSvg = anySvgElement.cloneNode(true) as Element;
        setMobileSvg(clonedSvg);
        setHasMobileSvg(true);
        setDesktopSvg(null);
        setHasDesktopSvg(false);
        setViewMode('mobile');
        foundAtLeastOne = true;
      }

      if (!foundAtLeastOne) {
        setError('No valid SVG found in content');
      } else {
        setError(null);
      }
    } catch (err) {
      console.error('Error parsing SVG:', err);
      setError('Failed to parse SVG');
    }
  }, [svgCode, isStreaming]);

  // Render the current SVG based on view mode
  // Note: svgCode is included in dependencies to ensure re-render on conversation change,
  // even if the parsed SVG state hasn't updated yet (prevents blank canvas on navigation)
  useEffect(() => {
    if (!containerRef.current) return;

    try {
      // Clear previous content
      containerRef.current.innerHTML = '';

      // Try to get SVG for current view mode, fall back to any available SVG
      let svgToRender = viewMode === 'mobile' ? mobileSvg : desktopSvg;

      // If the requested view mode's SVG doesn't exist, use the other one
      if (!svgToRender) {
        svgToRender = mobileSvg || desktopSvg;
      }

      if (svgToRender) {
        const cloned = svgToRender.cloneNode(true) as SVGSVGElement;

        // Ensure SVG has explicit dimensions for proper rendering
        // If SVG only has viewBox but no width/height, it may render at 0x0
        if (cloned instanceof SVGSVGElement) {
          const viewBox = cloned.getAttribute('viewBox');
          const hasWidth = cloned.hasAttribute('width');
          const hasHeight = cloned.hasAttribute('height');

          if (viewBox && (!hasWidth || !hasHeight)) {
            const [, , vbWidth, vbHeight] = viewBox.split(/\s+/).map(Number);
            if (!hasWidth && vbWidth) {
              cloned.setAttribute('width', String(vbWidth));
            }
            if (!hasHeight && vbHeight) {
              cloned.setAttribute('height', String(vbHeight));
            }
          }
        }

        containerRef.current.appendChild(cloned);
        setError(null);
      }
    } catch (err) {
      console.error('Error rendering SVG:', err);
      setError('Failed to render SVG');
    }
  }, [viewMode, mobileSvg, desktopSvg, svgCode]);

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 10, 10));
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(width);
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = startX - e.clientX;
      const viewportWidth = window.innerWidth;
      const deltaPercent = (deltaX / viewportWidth) * 100;
      const newWidth = Math.min(Math.max(startWidth + deltaPercent, 25), 75);

      if (onWidthChange) {
        onWidthChange(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, startX, startWidth, onWidthChange]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowVersionDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setShowExportDropdown(false);
      }
    };

    if (showVersionDropdown || showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVersionDropdown, showExportDropdown]);

  const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) {
      return 'N/A';
    }
    const month = dateObj.getMonth() + 1;
    const day = dateObj.getDate();
    const year = dateObj.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  };

  const selectedArtifact = artifacts[selectedArtifactIndex];

  const handleExportSVG = () => {
    try {
      // Get the currently displayed SVG element
      const svgElement = containerRef.current?.querySelector('svg');
      if (!svgElement) {
        console.error('No SVG element found for export');
        alert('No SVG available to export');
        return;
      }

      // Serialize the current SVG
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const blob = new Blob([svgData], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const viewSuffix = viewMode === 'mobile' ? '-mobile' : viewMode === 'desktop' ? '-desktop' : '';
      link.download = `${title || 'artifact'}${viewSuffix}-${Date.now()}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setShowExportDropdown(false);
      setShowSuccessAlert(true);
      void usageService.trackDownload('svg');
    } catch (err) {
      console.error('Error exporting SVG:', err);
      alert('Failed to export SVG. Please try again.');
    }
  };

  const handleExportPNG = () => {
    try {
      const svgElement = containerRef.current?.querySelector('svg');
      if (!svgElement) {
        console.error('No SVG element found for PNG export');
        alert('No SVG available to export');
        return;
      }

      // Get SVG dimensions
      const svgRect = svgElement.getBoundingClientRect();
      const svgWidth = svgElement.viewBox?.baseVal?.width || svgRect.width || 800;
      const svgHeight = svgElement.viewBox?.baseVal?.height || svgRect.height || 600;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas size based on SVG dimensions
      canvas.width = svgWidth;
      canvas.height = svgHeight;

      const svgData = new XMLSerializer().serializeToString(svgElement);
      const img = new Image();

      img.onload = () => {
        // Clear canvas and draw
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const viewSuffix = viewMode === 'mobile' ? '-mobile' : viewMode === 'desktop' ? '-desktop' : '';
            link.download = `${title || 'artifact'}${viewSuffix}-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setShowSuccessAlert(true);
            void usageService.trackDownload('png');
          }
        });
      };

      img.onerror = (err) => {
        console.error('Error loading SVG for PNG export:', err);
        alert('Failed to export PNG. Please try again.');
      };

      // Create data URL from SVG
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      img.src = svgUrl;

      setShowExportDropdown(false);
    } catch (err) {
      console.error('Error exporting PNG:', err);
      alert('Failed to export PNG. Please try again.');
    }
  };

  const handleExportJPEG = () => {
    try {
      const svgElement = containerRef.current?.querySelector('svg');
      if (!svgElement) {
        console.error('No SVG element found for JPEG export');
        alert('No SVG available to export');
        return;
      }

      // Get SVG dimensions
      const svgRect = svgElement.getBoundingClientRect();
      const svgWidth = svgElement.viewBox?.baseVal?.width || svgRect.width || 800;
      const svgHeight = svgElement.viewBox?.baseVal?.height || svgRect.height || 600;

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas size based on SVG dimensions
      canvas.width = svgWidth;
      canvas.height = svgHeight;

      const svgData = new XMLSerializer().serializeToString(svgElement);
      const img = new Image();

      img.onload = () => {
        // Fill with white background for JPEG
        if (ctx) {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        }

        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const viewSuffix = viewMode === 'mobile' ? '-mobile' : viewMode === 'desktop' ? '-desktop' : '';
            link.download = `${title || 'artifact'}${viewSuffix}-${Date.now()}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            setShowSuccessAlert(true);
            void usageService.trackDownload('jpeg');
          }
        }, 'image/jpeg', 0.95);
      };

      img.onerror = (err) => {
        console.error('Error loading SVG for JPEG export:', err);
        alert('Failed to export JPEG. Please try again.');
      };

      // Create data URL from SVG
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      img.src = svgUrl;

      setShowExportDropdown(false);
    } catch (err) {
      console.error('Error exporting JPEG:', err);
      alert('Failed to export JPEG. Please try again.');
    }
  };

  const handleExportFigma = async () => {
    try {
      // Check Figma export limit before exporting
      if (!canExportFigma()) {
        setFigmaLimitError(getFigmaExportLimitMessage());
        setShowExportDropdown(false);
        return;
      }

      // Get the currently displayed SVG element
      const svgElement = containerRef.current?.querySelector('svg');
      if (!svgElement) {
        console.error('No SVG element found for Figma export');
        alert('No SVG available to export');
        return;
      }

      // Serialize the current SVG and copy to clipboard
      const svgData = new XMLSerializer().serializeToString(svgElement);
      await navigator.clipboard.writeText(svgData);

      // Track the Figma export
      await trackFigmaExport();

      // Fire Google Ads conversion event for Figma export
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          send_to: 'AW-17685768588/a4mNCOSvka0cEIzTnfFB',
          value: 20.0,
          currency: 'USD',
          transaction_id: '',
        });
      }

      setShowExportDropdown(false);
      setFigmaLimitError(null);

      // Check if user has seen the guide before
      const hasSeenFigmaGuide = localStorage.getItem('hasSeenFigmaGuide');

      if (!hasSeenFigmaGuide) {
        // First time - show the guide
        setShowFigmaGuide(true);
        localStorage.setItem('hasSeenFigmaGuide', 'true');
      } else {
        // Subsequent times - show alert banner
        setShowFigmaAlert(true);
      }
    } catch (err) {
      console.error('Error exporting to Figma:', err);
      alert('Failed to export to Figma. Please try again.');
    }
  };

  const handleOpenFigmaGuide = () => {
    setShowFigmaAlert(false);
    setShowFigmaGuide(true);
  };

  return (
    <>
      {showSuccessAlert && (
        <div
          className="svg-canvas-alert-container"
          style={{ left: `calc(${100 - width}% - ${rightOffset}px)`, width: `${width}%` }}
        >
          <AlertBanner
            message="Your file has been downloaded."
            type="success"
            onClose={() => setShowSuccessAlert(false)}
          />
        </div>
      )}
      {figmaLimitError && (
        <div
          className="svg-canvas-alert-container"
          style={{ left: `calc(${100 - width}% - ${rightOffset}px)`, width: `${width}%` }}
        >
          <AlertBanner
            message={figmaLimitError}
            type="error"
            onClose={() => setFigmaLimitError(null)}
            autoClose={false}
          />
        </div>
      )}
      {showFigmaGuide && (
        <FigmaIntegrationGuide
          onClose={() => setShowFigmaGuide(false)}
          svgCode={(() => {
            const svgElement = containerRef.current?.querySelector('svg');
            if (svgElement) {
              return new XMLSerializer().serializeToString(svgElement);
            }
            return svgCode;
          })()}
        />
      )}
      <div className="svg-canvas" ref={canvasRef} style={{ width: `${width}%`, right: `${rightOffset}px` }}>
        <div className="svg-canvas-resize-handle" onMouseDown={handleMouseDown}>
          <GripVertical className="resize-handle-icon" size={30} />
        </div>
        <div className="svg-canvas-header">
        <div className="svg-canvas-title-container">
          <h5 className="svg-canvas-title">{title || 'SVG Preview'}</h5>
          {templateName && <div className="svg-canvas-subtitle">{templateName} Template</div>}
        </div>
      </div>
      {showFigmaAlert && (
        <AlertBanner
          message={
            <>
              Your design artifact has been copied to your clipboard. For further instructions, see our{' '}
              <span
                onClick={handleOpenFigmaGuide}
                className="figma-guide-link"
              >
                Figma Integration Guide
              </span>
              {' '}<ArrowRight className="figma-guide-link" />
            </>
          }
          type="success"
          onClose={() => setShowFigmaAlert(false)}
          className="figma-header-alert"
        />
      )}
      <div className="svg-canvas-content">
        {/* Fixed Controls Layer */}
        {artifacts.length > 0 && (
          <div className="artifact-version-history" ref={dropdownRef}>
            <button
              className="artifact-version-toggle"
              onClick={() => setShowVersionDropdown(!showVersionDropdown)}
            >
              <div className="artifact-version-header">
                <span className="artifact-version-icon"><ClockHistory size={16} /></span>
                <span className="artifact-version-label">ARTIFACT VERSION HISTORY</span>
                <ChevronDown className={`artifact-version-chevron ${showVersionDropdown ? 'open' : ''}`} />
              </div>
            </button>
            {selectedArtifact && (
              <div className="artifact-current-selection">
                {formatDate(selectedArtifact.timestamp)}: {selectedArtifact.title.toUpperCase()}
              </div>
            )}
            {showVersionDropdown && (
              <div className="artifact-version-dropdown">
                {artifacts.map((artifact, index) => (
                  <button
                    key={artifact.id}
                    className={`artifact-version-item ${index === selectedArtifactIndex ? 'active' : ''}`}
                    onClick={() => {
                      if (onArtifactSelect) {
                        onArtifactSelect(index);
                      }
                      setShowVersionDropdown(false);
                    }}
                  >
                    <div className="artifact-version-date">{formatDate(artifact.timestamp)}</div>
                    <div className="artifact-version-title">{artifact.title}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="export-dropdown-container" ref={exportDropdownRef}>
          <button
            className="export-toggle"
            onClick={() => setShowExportDropdown(!showExportDropdown)}
          >
            <BoxArrowUp size={16} />
            <span className="export-label">EXPORT</span>
            <ChevronDown className={`export-chevron ${showExportDropdown ? 'open' : ''}`} />
          </button>
          {showExportDropdown && (
            <div className="export-dropdown">
              <button className="export-item" onClick={handleExportPNG}>
                PNG
              </button>
              <button className="export-item" onClick={handleExportJPEG}>
                JPEG
              </button>
              <button className="export-item" onClick={handleExportSVG}>
                SVG
              </button>
              <button className="export-item" onClick={handleExportFigma}>
                Figma File
              </button>
            </div>
          )}
        </div>

        {(hasMobileSvg && hasDesktopSvg) && (
          <div className="svg-view-toggle">
            <button
              className={`svg-toggle-btn svg-toggle-mobile ${viewMode === 'mobile' ? 'active' : ''}`}
              onClick={() => setViewMode('mobile')}
              aria-label="Mobile view"
              title="Mobile"
            >
              <PhoneFill size={20} />
            </button>
            <div className="svg-toggle-divider" />
            <button
              className={`svg-toggle-btn svg-toggle-desktop ${viewMode === 'desktop' ? 'active' : ''}`}
              onClick={() => setViewMode('desktop')}
              aria-label="Desktop view"
              title="Desktop"
            >
              <Display size={20} />
            </button>
          </div>
        )}

        <div className="svg-zoom-controls">
          <button
            className="svg-zoom-btn svg-zoom-out"
            onClick={handleZoomOut}
            aria-label="Zoom out"
            disabled={zoomLevel <= 10}
          >
            <ZoomOut size={18} />
          </button>
          <button
            className="svg-zoom-level"
            onClick={handleResetZoom}
            aria-label="Reset zoom"
          >
            {zoomLevel}%
          </button>
          <button
            className="svg-zoom-btn svg-zoom-in"
            onClick={handleZoomIn}
            aria-label="Zoom in"
            disabled={zoomLevel >= 200}
          >
            <ZoomIn size={18} />
          </button>
        </div>

        {/* Scrollable SVG Container */}
        <div className="svg-scrollable-wrapper">
          {error ? (
            <div className="svg-canvas-error">
              <p>{error}</p>
            </div>
          ) : isStreaming && !svgCode ? (
            <div className="svg-lottie-loader">
              <DotLottieReact
                src="/assets/animations/svg-loading.lottie"
                loop
                autoplay
                className="svg-lottie-animation"
              />
            </div>
          ) : (
            <div
              ref={containerRef}
              className="svg-container svg-container-transformed"
              style={{ transform: `scale(${zoomLevel / 100})` }}
            />
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default SvgCanvas;
